/**
 * Charm Personalizer (v2 — Letter x Colour variants)
 *
 * Each letter+colour combination is its own Shopify variant with its own
 * stock and (ideally) its own product photo. The colour pills set an
 * "active colour"; tapping a letter tile adds {letter, color} using
 * whichever variant matches that pair. The letter grid re-renders its
 * images every time the active colour changes, since the same letter
 * looks different (different photo) per colour.
 *
 * Falls back to a CSS-tinted text glyph for any variant missing a photo,
 * so the picker never breaks due to incomplete image coverage.
 */
(function () {
  'use strict';

  function initCharmPersonalizer(root) {
    var modal = root.querySelector('[data-charm-modal]');
    if (!modal) return;

    // Move the modal to the end of <body> so its position:fixed isn't
    // trapped inside an ancestor that creates a new containing block.
    document.body.appendChild(modal);

    // Forward label-color CSS variables from the root element onto the modal,
    // since appendChild detaches the modal from .charm-personalizer and it can
    // no longer inherit CSS custom properties set via style="" on the root div.
    var rootStyle = root.style;
    var CSS_VARS_TO_FORWARD = [
      '--charm-oos-color',
      '--charm-low-stock-color',
      '--charm-preorder-color',
      '--charm-cream',
      '--charm-add-btn-color',
      '--charm-title-color',
      '--charm-collar-preview-label-color',
      '--charm-charm-preview-label-color',
      '--charm-grid-label-color',
      '--charm-pill-active-border-color',
      '--charm-swatch-active-color'
    ];
    CSS_VARS_TO_FORWARD.forEach(function (name) {
      var value = rootStyle.getPropertyValue(name).trim();
      if (value) modal.style.setProperty(name, value);
    });

    var maxCharms = parseInt(root.dataset.maxCharms || '8', 10);
    var lowStockThreshold = parseInt(root.dataset.lowStockThreshold || '0', 10);
    var preorderLabel = root.dataset.preorderLabel || 'Pre-order';
    var currencySymbol = root.dataset.currency || '$';
    var priceDetailEmptyText = root.dataset.priceDetailEmpty || 'Price varies by letter/colour';
    var allowOversell = root.dataset.allowOversell === 'true';

    // ---- Parse the variant JSON data island ----
    var variantDataEl = modal.querySelector('[data-charm-variants-json]');
    var variants = [];
    if (variantDataEl) {
      try {
        variants = JSON.parse(variantDataEl.textContent || '[]');
      } catch (e) {
        console.error('Charm Personalizer: could not parse variant JSON.', e);
      }
    }

    // Index variants by "LETTER|COLOR" for fast lookup.
    var variantIndex = {};
    var seenLetters = {};
    variants.forEach(function (v) {
      if (!v.letter || !v.color) return;
      variantIndex[v.letter + '|' + v.color] = v;
      seenLetters[v.letter] = true;
    });

    // Build the grid's tile list from whatever letter/symbol values actually
    // exist on the product, rather than a fixed A-Z — so extra options like
    // "Flower", "Heart", "Paw", "Star" automatically get a tile too. A-Z are
    // shown first (in order) if present, then any remaining symbol names
    // alphabetically after them.
    var ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').filter(function (l) {
      return seenLetters[l];
    });
    var extraSymbols = Object.keys(seenLetters)
      .filter(function (l) { return ALPHABET.indexOf(l) === -1; })
      .sort();
    ALPHABET = ALPHABET.concat(extraSymbols);

    var state = {
      collarColorName: null,
      collarColorHex: null,
      activeCharmColorName: null,
      activeCharmColorHex: null,
      charms: [] // array of { letter, color, variantId, imageUrl, price }
    };

    // ---- Element refs ----
    var openBtn = root.querySelector('[data-charm-open]');
    var closeEls = modal.querySelectorAll('[data-charm-close]');
    var collarSwatches = modal.querySelectorAll('[data-collar-swatch]');
    var charmPills = modal.querySelectorAll('[data-charm-color-pill]');
    var grid = modal.querySelector('[data-charm-grid]');
    var previewBand = modal.querySelector('[data-preview-band]');
    var previewCharms = modal.querySelector('[data-preview-charms]');
    var countEl = modal.querySelector('[data-charm-count]');
    var maxEl = modal.querySelector('[data-charm-max]');
    var clearBtn = modal.querySelector('[data-charm-clear]');
    var addBtn = modal.querySelector('[data-charm-add]');
    var priceDetailEl = modal.querySelector('[data-charm-price-detail]');

    if (maxEl) maxEl.textContent = maxCharms;

    // ---- Init default selections ----
    if (collarSwatches.length) {
      state.collarColorName = collarSwatches[0].dataset.colorName;
      state.collarColorHex = collarSwatches[0].dataset.colorHex;
    }
    if (charmPills.length) {
      state.activeCharmColorName = charmPills[0].dataset.colorName;
      state.activeCharmColorHex = charmPills[0].dataset.colorHex;
    }

    // ---- Open / close ----
    function openModal() {
      modal.hidden = false;
      document.documentElement.style.overflow = 'hidden';
    }
    function closeModal() {
      modal.hidden = true;
      document.documentElement.style.overflow = '';
    }
    if (openBtn) openBtn.addEventListener('click', openModal);
    closeEls.forEach(function (el) {
      el.addEventListener('click', closeModal);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hidden) closeModal();
    });

    // ---- Collar colour selection ----
    collarSwatches.forEach(function (swatch) {
      swatch.addEventListener('click', function () {
        collarSwatches.forEach(function (s) { s.classList.remove('is-selected'); });
        swatch.classList.add('is-selected');
        state.collarColorName = swatch.dataset.colorName;
        state.collarColorHex = swatch.dataset.colorHex;
        renderPreview();
      });
    });

    // ---- Charm color selection (sets the ACTIVE colour for the next tap) ----
    charmPills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        charmPills.forEach(function (p) { p.classList.remove('is-selected'); });
        pill.classList.add('is-selected');
        state.activeCharmColorName = pill.dataset.colorName;
        state.activeCharmColorHex = pill.dataset.colorHex;
        renderGrid(); // re-render tiles to show this colour's images/stock
      });
    });

    // ---- Clear ----
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        state.charms = [];
        renderPreview();
        renderCounterAndPrice();
        renderGrid(); // re-enable tiles that were disabled at max
      });
    }

    // ---- Remove a placed charm (delegated) ----
    previewCharms.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-remove-index]');
      if (!btn) return;
      var idx = parseInt(btn.dataset.removeIndex, 10);
      state.charms.splice(idx, 1);
      renderPreview();
      renderCounterAndPrice();
      renderGrid(); // a slot freed up, or stock-disabled state may need to change
    });

    /**
     * Renders the 26-letter grid for the CURRENTLY ACTIVE colour.
     * Each tile looks up its Letter+ActiveColour variant; if no such
     * variant exists at all, the tile is disabled (combo not offered).
     * If the variant exists but is out of stock, it's disabled with a label.
     * If the variant has no image, falls back to a CSS-tinted glyph.
     */
    function renderGrid() {
      var atMax = state.charms.length >= maxCharms;
      var activeColor = state.activeCharmColorName;
      var activeHex = state.activeCharmColorHex;

      // Count how many of each letter+colour combo are already placed
      var placedCounts = {};
      state.charms.forEach(function (charm) {
        var k = charm.letter + '|' + charm.color;
        placedCounts[k] = (placedCounts[k] || 0) + 1;
      });

      grid.innerHTML = ALPHABET.map(function (letter) {
        var key = letter + '|' + activeColor;
        var variant = variantIndex[key];
        var exists = !!variant;

        // Pre-order: variant exists, qty is 0, but inventory_policy is 'continue'
        var isPreorder = exists
          && variant.inventoryQuantity <= 0
          && variant.inventoryPolicy === 'continue';

        // Truly available: either has stock, or is a pre-order
        var available = exists && (variant.available || isPreorder);

        // How many of this exact letter+color are already placed
        var alreadyPlaced = placedCounts[key] || 0;

        // When oversell is off: block adding more of a letter than its stock qty
        // (unless it's a pre-order, which has no meaningful stock floor)
        var stockCapped = !allowOversell
          && !isPreorder
          && exists
          && variant.inventoryQuantity > 0
          && alreadyPlaced >= variant.inventoryQuantity;

        // Low stock: has stock but at or below threshold (not pre-order)
        var isLowStock = exists
          && !isPreorder
          && lowStockThreshold > 0
          && variant.inventoryQuantity > 0
          && variant.inventoryQuantity <= lowStockThreshold;

        var disabled = atMax || !exists || !available || stockCapped;

        var visual = '';
        if (exists && variant.imageUrl) {
          visual = '<img class="charm-tile__img" src="' + variant.imageUrl + '" alt="' + letter + '" loading="lazy">';
        } else {
          var tileGlyphClass = 'charm-tile__glyph' + (letter.length > 2 ? ' charm-tile__glyph--word' : '');
          visual = '<span class="' + tileGlyphClass + '" style="--current-charm-color:' + activeHex + '">' + letter + '</span>';
        }

        var statusLabel = '';
        if (!exists) {
          statusLabel = ' <em>(unavailable)</em>';
        } else if (isPreorder) {
          statusLabel = ' <em class="is-preorder">' + preorderLabel + '</em>';
        } else if (!available) {
          statusLabel = ' <em>(out of stock)</em>';
        } else if (stockCapped) {
          statusLabel = ' <em class="is-low-stock">max reached</em>';
        } else if (isLowStock) {
          statusLabel = ' <em class="is-low-stock">' + variant.inventoryQuantity + ' left</em>';
        }

        return (
          '<button type="button" class="charm-tile' + (disabled ? ' is-disabled' : '') + (isPreorder ? ' is-preorder' : '') + '"' +
          ' data-charm-letter="' + letter + '"' +
          (exists ? ' data-variant-id="' + variant.variantId + '"' : '') +
          ' data-available="' + available + '"' +
          (disabled ? ' disabled' : '') +
          '>' +
          visual +
          '<span class="charm-tile__label">' + letter + statusLabel + '</span>' +
          '</button>'
        );
      }).join('');

      // Wire click handlers (grid is rebuilt each time, so re-bind every render)
      grid.querySelectorAll('[data-charm-letter]').forEach(function (tile) {
        tile.addEventListener('click', function () {
          if (tile.disabled || tile.classList.contains('is-disabled')) return;
          if (state.charms.length >= maxCharms) return;

          var letter = tile.dataset.charmLetter;
          var key = letter + '|' + activeColor;
          var variant = variantIndex[key];
          if (!variant) return;

          state.charms.push({
            letter: letter,
            color: activeColor,
            variantId: variant.variantId,
            imageUrl: variant.imageUrl,
            price: variant.price,
            sellingPlanId: variant.sellingPlanId || null
          });

          renderPreview();
          renderCounterAndPrice();
          renderGrid();
        });
      });
    }

    /** Renders the live preview strip — each chip shows its OWN colour's photo or glyph. */
    function renderPreview() {
      if (state.collarColorHex) {
        previewBand.style.setProperty('--current-collar-color', state.collarColorHex);
      } else {
        // No collar swatches (preview hidden) — clear the property so the
        // CSS fallback applies rather than writing an invalid value.
        previewBand.style.removeProperty('--current-collar-color');
      }

      previewCharms.innerHTML = state.charms
        .map(function (charm, i) {
          var previewGlyphClass = 'preview-charm__letter' + (charm.letter.length > 2 ? ' preview-charm__letter--word' : '');
          var visual = charm.imageUrl
            ? '<img class="preview-charm__img" src="' + charm.imageUrl + '" alt="' + charm.letter + '">'
            : '<span class="' + previewGlyphClass + '" style="--current-charm-color:' + (findColorHex(charm.color) || '#F0D878') + '">' + charm.letter + '</span>';

          return (
            '<span class="preview-charm">' +
            '<span class="preview-charm__visual">' + visual + '</span>' +
            '<button type="button" class="preview-charm__remove" data-remove-index="' + i + '" aria-label="Remove ' + charm.letter + '">&times;</button>' +
            '</span>'
          );
        })
        .join('');

      // Scroll to end so the latest charm is always visible
      previewCharms.scrollLeft = previewCharms.scrollWidth;

      // Switch to left-align only when content actually overflows,
      // so short sequences stay centered on the band
      previewCharms.classList.toggle(
        'is-overflowing',
        previewCharms.scrollWidth > previewCharms.clientWidth
      );
    }

    function findColorHex(colorName) {
      var match = null;
      charmPills.forEach(function (pill) {
        if (pill.dataset.colorName === colorName) match = pill.dataset.colorHex;
      });
      return match;
    }

    function renderCounterAndPrice() {
      countEl.textContent = state.charms.length;

      // Sum each placed charm's REAL variant price (Shopify stores prices
      // in cents, so divide by 100) instead of assuming a flat per-charm rate.
      var totalCents = state.charms.reduce(function (sum, charm) {
        return sum + (charm.price || 0);
      }, 0);
      var total = (totalCents / 100).toFixed(2);

      priceDetailEl.textContent =
        state.charms.length > 0
          ? state.charms.length + ' charm' + (state.charms.length === 1 ? '' : 's') + ' = ' + currencySymbol + total
          : priceDetailEmptyText;
      addBtn.disabled = state.charms.length === 0;
    }

    // ---- Initial render ----
    renderGrid();
    renderPreview();
    renderCounterAndPrice();

    // ---- Add to cart ----
    addBtn.addEventListener('click', function () {
      if (state.charms.length === 0) return;
      addBtn.disabled = true;
      addBtn.querySelector('.charm-add-btn__title').textContent = 'Adding\u2026';

      var collarVariantId = getSelectedCollarVariantId();
      if (!collarVariantId) {
        alert('Please select a size before adding to cart.');
        addBtn.disabled = false;
        addBtn.querySelector('.charm-add-btn__title').textContent = 'Add to Set';
        return;
      }

      // Group by variantId+sellingPlanId so a pre-order charm and a normal
      // charm of the same variant stay as separate cart line items.
      var grouped = {};
      state.charms.forEach(function (charm) {
        var groupKey = charm.variantId + '|' + (charm.sellingPlanId || '');
        if (!grouped[groupKey]) {
          grouped[groupKey] = {
            variantId: charm.variantId,
            quantity: 0,
            letter: charm.letter,
            color: charm.color,
            sellingPlanId: charm.sellingPlanId || null
          };
        }
        grouped[groupKey].quantity += 1;
      });

      var sequenceLabel = state.charms
        .map(function (c) { return c.letter + ' (' + c.color + ')'; })
        .join(', ');

      var items = [
        {
          id: parseInt(collarVariantId, 10),
          quantity: 1,
          properties: {
            'Charms': sequenceLabel
          }
        }
      ];

      var overflowItems = [];

      Object.keys(grouped).forEach(function (groupKey) {
        var group = grouped[groupKey];
        var variantId = parseInt(group.variantId, 10);
        var totalQty = group.quantity;

        // Look up the variant's current stock so we can split at the boundary
        var lookupKey = group.letter + '|' + group.color;
        var variantData = variantIndex[lookupKey];
        var stockQty = variantData ? variantData.inventoryQuantity : totalQty;
        var sellingPlanId = variantData ? variantData.sellingPlanId : null;
        var canOversell = variantData && variantData.inventoryPolicy === 'continue';

        var inStockQty = Math.min(totalQty, Math.max(stockQty, 0));
        var overflowQty = totalQty - inStockQty;

        // In-stock portion: goes in the main items batch (no selling plan)
        if (inStockQty > 0) {
          items.push({
            id: variantId,
            quantity: inStockQty,
            properties: {
              '_For Collar Colour': state.collarColorName,
              '_Sequence': sequenceLabel
            }
          });
        }

        // Overflow portion: queued for a SEPARATE cart/add.js request after
        // the first one succeeds — Shopify enforces inventory per-request,
        // so splitting into two sequential calls bypasses the cap correctly.
        if (overflowQty > 0 && canOversell) {
          var overflowItem = {
            id: variantId,
            quantity: overflowQty,
            properties: {
              '_For Collar Colour': state.collarColorName,
              '_Sequence': sequenceLabel,
              '_Type': preorderLabel
            }
          };
          if (sellingPlanId) overflowItem.selling_plan = sellingPlanId;
          overflowItems.push(overflowItem);
        }
      });


      // Helper: POST to cart/add.js
      function cartAdd(payload) {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: payload })
        }).then(function (res) {
          if (!res.ok) return res.json().then(function (err) { throw err; });
          return res.json();
        });
      }

      // Properties injected by the pre-order app that we strip from lines that
      // are NOT actually pre-orders (i.e. the in-stock portion, which carries
      // no selling plan). The app tags every line of a campaign variant, so a
      // partially-oversold letter would otherwise show "Note: Preorder" on the
      // in-stock line too.
      var PREORDER_PROP_KEYS = ['Note', '_essential_preorder_campaign_id'];

      // Rewrite the properties of one cart line, identified by its line key.
      // cart/change.js REPLACES the properties object, so omitting a key drops it.
      function cartChangeProps(lineKey, properties) {
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: lineKey, properties: properties })
        }).then(function (res) {
          if (!res.ok) return res.json().then(function (err) { throw err; });
          return res.json();
        });
      }

      // After both adds, drop the pre-order tags from our in-stock charm lines.
      // We only touch lines that are ours (carry _Sequence) AND have no selling
      // plan — a real pre-order line keeps its tags untouched.
      function scrubPreorderTagsFromInStockLines() {
        return fetch('/cart.js')
          .then(function (res) { return res.json(); })
          .then(function (cart) {
            var lines = (cart.items || []).filter(function (item) {
              var props = item.properties || {};
              var isOurs = Object.prototype.hasOwnProperty.call(props, '_Sequence');
              var isPreorderLine = !!item.selling_plan_allocation;
              var hasAppTags = PREORDER_PROP_KEYS.some(function (k) {
                return Object.prototype.hasOwnProperty.call(props, k);
              });
              return isOurs && !isPreorderLine && hasAppTags;
            });

            // Rewrite each affected line in sequence (cart mutations must not race).
            return lines.reduce(function (chain, item) {
              return chain.then(function () {
                var cleaned = {};
                Object.keys(item.properties).forEach(function (k) {
                  if (PREORDER_PROP_KEYS.indexOf(k) === -1) cleaned[k] = item.properties[k];
                });
                return cartChangeProps(item.key, cleaned);
              });
            }, Promise.resolve());
          })
          .catch(function (err) {
            // Non-fatal: the item is in the cart, it just kept an extra note.
            console.warn('Charm Personalizer: could not clean pre-order tags.', err);
          });
      }

      // Send main items first, then overflow items in a second request
      cartAdd(items)
        .then(function (data) {
          if (overflowItems.length > 0) {
            return cartAdd(overflowItems);
          }
        })
        .then(function () {
          // Let the pre-order app's script finish tagging before we scrub.
          return new Promise(function (resolve) { setTimeout(resolve, 400); });
        })
        .then(scrubPreorderTagsFromInStockLines)
        .then(function (data) {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          closeModal();
          addBtn.disabled = false;
          addBtn.querySelector('.charm-add-btn__title').textContent = 'Add to Set';
          state.charms = [];
          renderPreview();
          renderCounterAndPrice();
          renderGrid();
        })
        .catch(function (err) {
          console.error('Charm Personalizer add-to-cart failed:', err);
          alert((err && err.description) || 'Could not add to cart. Please try again.');
          addBtn.disabled = false;
          addBtn.querySelector('.charm-add-btn__title').textContent = 'Add to Set';
        });
    });
  }

  function getSelectedCollarVariantId() {
    var input = document.querySelector('form[action*="/cart/add"] input[name="id"]');
    return input ? input.value : null;
  }

  function boot() {
    document.querySelectorAll('[data-charm-personalizer]').forEach(initCharmPersonalizer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();